import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";

import localLawService from "api/localLawService";
import {
  ELegalActStatus,
  ILegalActType,
  ISaveLegalActMetricRequest,
} from "dto/LocalLaw/ILocalLaw";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

interface Props {
  actId: string;
  types: ILegalActType[];
  onClose: () => void;
  onSaved: () => void;
}

// Metryka aktu prawa miejscowego — odwzorowanie okna Metric z Magistratu
// (Kwantum.Documents.UI/Metric: MetricDescriptionTab + MetricLegalActDatesTab).
//
// Zakres zgodny z ustaleniami migracji: data, tytuł, rodzaj, data aktu, data
// uchwalenia, status, publikacja w BIP (osobne okno) oraz rozstrzygnięcie nadzorcze
// (osobne okno). Doszły daty publikacji/wejścia w życie i pozycja w dzienniku
// urzędowym, bo bez nich nie da się złożyć poprawnej metryki XML.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const toDateInput = (iso: string | null | undefined) =>
  iso ? new Date(iso).toISOString().slice(0, 10) : "";

const formatDateTime = (iso: string) => new Date(iso).toLocaleString("pl-PL");

const STATUSES: { value: ELegalActStatus; label: string }[] = [
  { value: ELegalActStatus.Projekt, label: "Projekt" },
  { value: ELegalActStatus.DoOpracowania, label: "Do opracowania" },
  { value: ELegalActStatus.WOpracowaniu, label: "W opracowaniu" },
  { value: ELegalActStatus.DoKonsultacji, label: "Do konsultacji" },
  {
    value: ELegalActStatus.DoUchwaleniaNaNajblizszejSesji,
    label: "Do uchwalenia na najbliższej sesji",
  },
  { value: ELegalActStatus.Uchwalony, label: "Uchwalony" },
  { value: ELegalActStatus.Przyjety, label: "Przyjęty" },
  { value: ELegalActStatus.Ogloszony, label: "Ogłoszony" },
  { value: ELegalActStatus.Uniewazniony, label: "Unieważniony" },
  { value: ELegalActStatus.Archiwalny, label: "Archiwalny" },
];

const MetricModal = ({ actId, types, onClose, onSaved }: Props) => {
  const actQuery = localLawService.useGetAct(actId);
  const { mutate: saveMetric, isLoading: saving } = localLawService.useSaveMetric();

  const [form, setForm] = useState<ISaveLegalActMetricRequest | null>(null);

  const act = actQuery.data?.data;

  useEffect(() => {
    if (!act) return;

    setForm({
      ActType: act.ActType,
      ActNumber: act.ActNumber,
      Title: act.Title,
      Author: act.Author,
      ActStatus: act.ActStatus,
      Date: toDateInput(act.Date) || null,
      AdoptedDate: toDateInput(act.AdoptedDate) || null,
      PublicationDate: toDateInput(act.PublicationDate) || null,
      EffectiveFrom: toDateInput(act.EffectiveFrom) || null,
      EffectiveTo: toDateInput(act.EffectiveTo) || null,
      BackDate: toDateInput(act.BackDate) || null,
      IsNormative: act.IsNormative,
      IsIndependent: act.IsIndependent,
      IsArchival: act.IsArchival,
      JournalName: act.JournalName,
      JournalYear: act.JournalYear,
      JournalPosition: act.JournalPosition,
    });
  }, [act]);

  const set = <K extends keyof ISaveLegalActMetricRequest>(
    key: K,
    value: ISaveLegalActMetricRequest[K],
  ) => setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const submit = () => {
    if (!form) return;

    saveMetric(
      { id: actId, request: form },
      {
        onSuccess: onSaved,
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się zapisać metryki."),
          }),
      },
    );
  };

  const readOnly = !!act && !act.CanEdit;

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTitle}>Metryka aktu</div>

        <div className={styles.modalBody}>
          {actQuery.isLoading || !form ? (
            <Loading />
          ) : (
            <>
              {readOnly && (
                <span className={styles.hint}>
                  Akt jest poza Twoją redakcją — metryka jest tylko do odczytu.
                </span>
              )}

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Rodzaj aktu</label>
                  <select
                    className={styles.textInput}
                    value={form.ActType}
                    disabled={readOnly}
                    onChange={(e) => set("ActType", Number(e.target.value))}
                  >
                    {types.map((t) => (
                      <option key={t.Value} value={t.Value}>
                        {t.Name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Numer</label>
                  <input
                    className={styles.textInput}
                    value={form.ActNumber ?? ""}
                    disabled={readOnly}
                    onChange={(e) => set("ActNumber", e.target.value || null)}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Status</label>
                  <select
                    className={styles.textInput}
                    value={form.ActStatus}
                    disabled={readOnly}
                    onChange={(e) =>
                      set("ActStatus", Number(e.target.value) as ELegalActStatus)
                    }
                  >
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>Przedmiot regulacji</label>
                <textarea
                  className={styles.textArea}
                  value={form.Title ?? ""}
                  disabled={readOnly}
                  onChange={(e) => set("Title", e.target.value || null)}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>Organ wydający (dopełniacz)</label>
                <input
                  className={styles.textInput}
                  value={form.Author ?? ""}
                  disabled={readOnly}
                  onChange={(e) => set("Author", e.target.value || null)}
                />
              </div>

              <div className={styles.sectionTitle}>Daty</div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Data aktu</label>
                  <input
                    className={styles.textInput}
                    type="date"
                    value={form.Date ?? ""}
                    disabled={readOnly}
                    onChange={(e) => set("Date", e.target.value || null)}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Data uchwalenia</label>
                  <input
                    className={styles.textInput}
                    type="date"
                    value={form.AdoptedDate ?? ""}
                    disabled={readOnly}
                    onChange={(e) => set("AdoptedDate", e.target.value || null)}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Data publikacji</label>
                  <input
                    className={styles.textInput}
                    type="date"
                    value={form.PublicationDate ?? ""}
                    disabled={readOnly}
                    onChange={(e) => set("PublicationDate", e.target.value || null)}
                  />
                </div>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Wejście w życie</label>
                  <input
                    className={styles.textInput}
                    type="date"
                    value={form.EffectiveFrom ?? ""}
                    disabled={readOnly}
                    onChange={(e) => set("EffectiveFrom", e.target.value || null)}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Koniec obowiązywania</label>
                  <input
                    className={styles.textInput}
                    type="date"
                    value={form.EffectiveTo ?? ""}
                    disabled={readOnly}
                    onChange={(e) => set("EffectiveTo", e.target.value || null)}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Moc wsteczna</label>
                  <input
                    className={styles.textInput}
                    type="date"
                    value={form.BackDate ?? ""}
                    disabled={readOnly}
                    onChange={(e) => set("BackDate", e.target.value || null)}
                  />
                </div>
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.IsNormative}
                    disabled={readOnly}
                    onChange={(e) => set("IsNormative", e.target.checked)}
                  />
                  Akt normatywny (podlega publikacji w dzienniku urzędowym)
                </label>

                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.IsIndependent}
                    disabled={readOnly}
                    onChange={(e) => set("IsIndependent", e.target.checked)}
                  />
                  Akt samoistny
                </label>

                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.IsArchival}
                    disabled={readOnly}
                    onChange={(e) => set("IsArchival", e.target.checked)}
                  />
                  Archiwalny
                </label>
              </div>

              <div className={styles.sectionTitle}>Pozycja w dzienniku urzędowym</div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Dziennik</label>
                  <input
                    className={styles.textInput}
                    value={form.JournalName ?? ""}
                    disabled={readOnly}
                    placeholder="np. Dz. Urz. Woj. Mazowieckiego"
                    onChange={(e) => set("JournalName", e.target.value || null)}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Rok</label>
                  <input
                    className={styles.textInput}
                    type="number"
                    value={form.JournalYear ?? ""}
                    disabled={readOnly}
                    onChange={(e) =>
                      set("JournalYear", e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Pozycja</label>
                  <input
                    className={styles.textInput}
                    type="number"
                    value={form.JournalPosition ?? ""}
                    disabled={readOnly}
                    onChange={(e) =>
                      set(
                        "JournalPosition",
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </div>
              </div>

              {!!act?.Attachments.length && (
                <>
                  <div className={styles.sectionTitle}>Załączniki aktu</div>
                  {act.Attachments.map((a) => (
                    <div key={a.Id} className={styles.attachmentRow}>
                      <FontAwesomeIcon icon={faPaperclip} />
                      <span style={{ flex: 1 }}>
                        {a.Number ? `Nr ${a.Number} — ` : ""}
                        {a.Title || a.FileName || "załącznik"}
                      </span>
                      <span className={styles.attachmentKind}>{a.KindName}</span>
                    </div>
                  ))}
                  <span className={styles.hint}>
                    Załączniki wstawia się w treści aktu w edytorze Word — tutaj są tylko
                    do wglądu.
                  </span>
                </>
              )}

              <div className={styles.sectionTitle}>Przebieg obiegu</div>

              <div className={styles.timeline}>
                {(act?.Log ?? []).map((entry) => (
                  <div key={entry.Id} className={styles.timelineRow}>
                    <span className={styles.timelineAction}>{entry.ActionName}</span>
                    <span>
                      {formatDateTime(entry.When)}
                      {entry.Who ? ` · ${entry.Who}` : ""}
                      {entry.Target ? ` → ${entry.Target}` : ""}
                      {entry.Comment ? ` · ${entry.Comment}` : ""}
                    </span>
                  </div>
                ))}
                {!act?.Log.length && (
                  <span className={styles.hint}>Brak zdarzeń w obiegu.</span>
                )}
              </div>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text={readOnly ? "Zamknij" : "Anuluj"} onClick={onClose} />
          {!readOnly && (
            <Button text="Zapisz" loading={saving} disabled={!form} onClick={submit} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricModal;
